<script setup>
import { computed, onBeforeMount, onBeforeUnmount, reactive, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useClientRequests } from "../../Composables/useClientRequests.js"
import { useAuthUserStore } from "../../Store/AuthUserStore.js"
import _ from 'lodash'
import axios from "axios"
import DataTable from "../../Components/DataTable.vue"
import Modal from "../../Components/Modal.vue"
import FormSelectField from "../../Components/FormSelectField.vue"

const { httpErrorsHandler } = useHttpErrorsHandler()

const storageKey = "Shiftech_ClientRequestOtherList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const authUserStore = useAuthUserStore()
const authUser = computed(() => authUserStore.authUser)

const filters = reactive({
    center_id: null,
    status: null,
    tag: null,
})
const filtersApplied = ref(true)
const centers = ref([])
const errors = ref(null)

const {
    url,
    data: clientRequestsOther,
    links,
    meta,
    loading,
    query,
    searchLoaded,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    search,
    loadData,
} = useDataManager({
    baseUrl: '/api/client-requests?only_others=true',
    baseSearchUrl: '/api/search/client-requests?only_others=true',
    defaultSortingField: 'updated_at',
    defaultSortingFieldsOrder: {
        id: 'asc',
        updated_at: 'asc',
        last_name: 'asc',
        notifications_center: 'asc',
        contact_reason: 'asc',
    },
    filters,
    onError: (error) => httpErrorsHandler(error),
})

const debouncedSearch = _.debounce(search, 250)

const columns = ref([
    { label: '#ID', field: 'id', sortable: true, slot: 'id', class: 'col-1 fw-bold' },
    { label: 'Nom client', slot: 'client_name', class: 'col-1' },
    { label: 'Raison contact', field: 'contact_reason', sortable: true, slot: 'contact_reason', class: 'col-1' },
    { label: 'Véhicule', slot: 'vehicle', class: 'col-2' },
    { label: 'Prestation', slot: 'car_stage', class: 'col-1' },
    { label: 'Centre', slot: 'center', class: 'col-1' },
    { label: 'Statut', slot: 'status', class: 'col-1' },
    { label: 'Dernière MAJ', field: 'updated_at', sortable: true, slot: 'updated_at', class: 'col-1' },
    { label: 'Notifications', field: 'notifications_center', sortable: true, slot: 'notifications', class: 'col-1 text-center' },
    { label: 'Détails', slot: 'details', class: 'col-1 text-center' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

const clientRequestStatuses = [
    { value: 'pending', label: 'À traiter', badgeColor: 'danger' },
    { value: 'responded', label: 'Répondu', badgeColor: 'secondary' },
    { value: 'parts_ordered', label: 'Pièces commandées', badgeColor: 'info' },
    { value: 'appointment_scheduled', label: 'RDV programmé', badgeColor: 'success' },
    { value: 'appointment_ongoing', label: 'RDV en cours', badgeColor: 'success' },
    { value: 'won', label: 'Gagné', badgeColor: 'success' },
    { value: 'no_show', label: 'Client non présenté', badgeColor: 'dark' },
    { value: 'lost', label: 'Perdu', badgeColor: 'dark' },
    { value: 'closed', label: 'Fermé', badgeColor: 'dark' },
]

onBeforeMount(() => {
    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        url.value = savedSettings.url || url.value
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
        query.value = savedSettings.query || ''
        searchLoaded.value = savedSettings.searchLoaded || false
        if (savedSettings.filters) {
            Object.assign(filters, savedSettings.filters)
        }
        filtersApplied.value = savedSettings.filtersApplied !== false
    }

    loadData(url.value)
    getCenters()
})

watch(
    [url, sortingField, sortingFieldsOrder, query, searchLoaded],
    ([newUrl, newSortingField, newSortingOrder, newQuery, newSearchLoaded]) => {
        saveToLocalStorage({
            url: newUrl,
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
            query: newQuery,
            searchLoaded: newSearchLoaded,
            filters: { ...filters },
            filtersApplied: filtersApplied.value,
        })
    },
    { deep: true }
)

watch(
    () => [filters.center_id, filters.status, filters.tag],
    () => {
        filtersApplied.value = false
    },
    { deep: true }
)

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

const idToDelete = ref(null)

function showModal(modalId, id) {
    idToDelete.value = id
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

const {
    deleteClientRequest,
    changeStatus,
} = useClientRequests({
    onDeleted: () => {
        loadData(url.value)
        hideModal('modalDelete')
    },
    onStatusChanged: (id, status) => {
        const index = clientRequestsOther.value.findIndex(cr => cr.data.id === id)
        if (index !== -1) {
            const statusInfo = clientRequestStatuses.find(s => s.value === status)
            clientRequestsOther.value[index].data.status = status
            clientRequestsOther.value[index].data.status_label_fr = statusInfo.label
            clientRequestsOther.value[index].data.status_badge_color = statusInfo.badgeColor
            if (status === 'closed') {
                clientRequestsOther.value[index].data.notifications_center = 0
            }
        }
    },
    onError: (error) => httpErrorsHandler(error),
})

function handleChangeStatus(status, id) {
    const currentRequest = clientRequestsOther.value.find(cr => cr.data.id === id)
    if (currentRequest && status === currentRequest.data.status) {
        return
    }
    changeStatus(id, status)
}

function applyFilter() {
    filtersApplied.value = true
    saveToLocalStorage({
        url: url.value,
        sortingField: sortingField.value,
        sortingFieldsOrder: { ...sortingFieldsOrder },
        query: query.value,
        searchLoaded: searchLoaded.value,
        filters: { ...filters },
        filtersApplied: true,
    })
    loadData(url.value)
}

function getCenters() {
    centers.value = [{
        text: 'Tous les centres',
        value: null
    }]
    axios.get('/api/centers?sortingField=name&sortingOrder=asc&all==true')
        .then(response => {
            response.data.data.forEach(center => {
                centers.value.push({
                    text: center.data.name,
                    value: center.data.id
                })
            })
        })
        .catch(error => {
            httpErrorsHandler(error)
        })
}
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-header border border-dashed border-top-0 border-start-0 border-end-0">
                    <div class="d-flex align-items-center">
                        <h5 class="card-title mb-0 flex-grow-1">Toutes les autres demandes</h5>
                        <router-link :to="{ name:'client-requests.create' }" class="btn btn-secondary" v-if="authUser.can && authUser.can.create_client_request">
                            <i class="ri-add-fill me-1 align-bottom hide-on-mobile"></i> Ajouter
                        </router-link>
                    </div>
                </div>

                <!-- Filters -->
                <div class="card-body">
                    <div class="row g-3">
                        <div class="col-xxl-3">
                            <div class="search-box">
                                <input type="search" class="form-control search" placeholder="Rechercher..." v-model="query" @input="debouncedSearch" id="searchClientRequestsOther">
                                <i class="ri-search-line search-icon"></i>
                            </div>
                        </div>

                        <form-select-field
                            name="filters.status"
                            label="Statut"
                            :label-hidden="true"
                            @update:field="filters.status = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.status"
                            bs-class="col-xxl-3 col-sm-6"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            placeholder="Tous les statuts"
                            :liste-options="[
                                { text: 'Statuts ouverts (A traiter, Répondu...)', value: 'all_open' },
                                { text: 'Statuts fermés (Fermé, Client non présenté...)', value: 'all_closed' },
                                { text: 'Tous les statuts', value: null },
                                { text: '---', value: null },
                                { text: 'À traiter', value: 'pending' },
                                { text: 'Répondu', value: 'responded' },
                                { text: 'Pièces commandées', value: 'parts_ordered' },
                                { text: 'RDV programmé', value: 'appointment_scheduled' },
                                { text: 'RDV en cours', value: 'appointment_ongoing' },
                                { text: 'Gagné', value: 'won' },
                                { text: 'Client non présenté', value: 'no_show' },
                                { text: 'Perdu', value: 'lost' },
                                { text: 'Fermé', value: 'closed' }
                            ]"
                            margin="mb-0"
                        />

                        <form-select-field
                            name="filters.tag"
                            label="Tag"
                            :label-hidden="true"
                            @update:field="filters.tag = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.tag"
                            bs-class="col-xxl-3 col-sm-6"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            placeholder="Tous les tags"
                            :liste-options="[
                                { text: 'Tous les tags', value: null },
                                { text: '---', value: null },
                                { text: 'Promotion', value: 'Promotion' },
                                { text: 'ECU en dev', value: 'ECU en dev' },
                                { text: 'SAV', value: 'SAV' },
                                { text: 'Remise Prépa', value: 'Remise Prépa' },
                                { text: 'Remise origine', value: 'Remise origine' },
                                { text: 'Franchise', value: 'Franchise' },
                                { text: 'Jobs', value: 'Jobs' },
                                { text: 'Jeux-concours', value: 'Jeux-concours' },
                                { text: 'Doublon', value: 'Doublon' },
                                { text: 'Tournage vidéo Youtube', value: 'Tournage vidéo Youtube' }
                            ]"
                            margin="mb-0"
                        />

                        <form-select-field
                            name="filters.center_id"
                            label="Centre"
                            :label-hidden="true"
                            @update:field="filters.center_id = $event"
                            :errors="errors"
                            :required="true"
                            :data="filters.center_id"
                            bs-class="col-xxl-2 col-sm-10"
                            :disabled="false"
                            additional-class="bg-light border-light"
                            placeholder="Tous les centres"
                            :liste-options="centers"
                            margin="mb-0"
                        />

                        <div class="col-xxl-1 col-sm-2">
                            <button type="button" class="btn w-100" @click.prevent="applyFilter" :class="filtersApplied ? 'btn-dark' : 'btn-warning'">
                                <i class="ri-equalizer-fill me-1 align-bottom hide-on-mobile"></i>
                                Filtrer
                            </button>
                        </div>
                    </div>
                </div>

                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="clientRequestsOther"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        :search-loaded="searchLoaded"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <!-- ID -->
                        <template #id="{ item }">
                            <span class="fw-medium text-dark">#{{ item.data.id }}</span>
                        </template>

                        <!-- Client Name -->
                        <template #client_name="{ item }">
                            {{ item.data.client.data.last_name }} {{ item.data.client.data.first_name }}
                        </template>

                        <!-- Contact Reason -->
                        <template #contact_reason="{ item }">
                            {{ item.data.contact_reason }}
                        </template>

                        <!-- Vehicle -->
                        <template #vehicle="{ item }">
                            {{ item.data.vehicle_brand_name }} - {{ item.data.vehicle_model_name }}
                        </template>

                        <!-- Car Stage -->
                        <template #car_stage="{ item }">
                            {{ item.data.car_stage }}
                        </template>

                        <!-- Center -->
                        <template #center="{ item }">
                            {{ item.data.center.data.name }}
                        </template>

                        <!-- Status -->
                        <template #status="{ item }">
                            <span :class="'badge fs-11 bg-' + item.data.status_badge_color + '-subtle text-' + item.data.status_badge_color">{{ item.data.status_label_fr }}</span>
                            <a href="#" class="ri-pencil-fill fs-17 px-1 align-middle text-info ms-1" :id="'statusDropdown-' + item.data.id" data-bs-toggle="dropdown" v-if="item.can.update"></a>
                            <ul class="dropdown-menu" :aria-labelledby="'statusDropdown-' + item.data.id">
                                <li v-for="status in clientRequestStatuses" :key="status.value">
                                    <a class="dropdown-item" href="#" @click.prevent="handleChangeStatus(status.value, item.data.id)">
                                        <span :class="`fs-11 badge bg-${status.badgeColor}-subtle text-${status.badgeColor}`">{{ status.label }}</span>
                                    </a>
                                </li>
                            </ul>
                        </template>

                        <!-- Updated At -->
                        <template #updated_at="{ item }">
                            {{ item.data.updated_at_fr }}
                        </template>

                        <!-- Notifications -->
                        <template #notifications="{ item }">
                            <span v-if="item.data.notifications_center > 0" class="badge fs-11 rounded-pill bg-danger-subtle text-danger">{{ item.data.notifications_center }}</span>
                        </template>

                        <!-- Details -->
                        <template #details="{ item }">
                            <router-link :to="{ name: 'client-request-others.show', params: { id: item.data.id } }" class="btn btn-secondary bg-gradient waves-effect waves-light btn-sm">Accéder</router-link>
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <!-- UPDATE -->
                            <router-link :to="{ name: 'client-requests.edit', params: { id: item.data.id } }" class="text-info" title="Modifier la demande" v-if="item.can.update">
                                <i class="ri-pencil-fill fs-17 px-1"></i>
                            </router-link>
                            <i class="ri-pencil-fill fs-17 px-1 text-muted" v-else></i>

                            <!-- DELETE -->
                            <a href="#" class="text-danger" title="Supprimer la demande" @click.prevent="showModal('modalDelete', item.data.id)" v-if="item.can.delete">
                                <i class="ri-delete-bin-3-fill fs-17 px-1"></i>
                            </a>
                            <i class="ri-delete-bin-3-fill fs-17 px-1 text-muted" v-else></i>
                        </template>
                    </data-table>

                    <modal id="modalDelete" title="Supprimer la demande client ?" btn-class="btn-danger" btn-text="Supprimer" @click:button="deleteClientRequest(idToDelete)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous êtes sur le point de supprimer une demande client.</p>
                        </template>
                    </modal>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
